import { securedWrap } from '@mop/shared/utils/securedWrap';
import { isClient, scrollToPosition } from '@mop/shared/utils/util';
import { ssrCache } from '@/api/utils/ssrCache';
import type { CmsStoriesParams, CmsStoryListResponseData } from '@/types/cms';
import { cmsStoryListModel } from '@/models';

type StoryListPageOptions = {
  cacheKeyPrefix: string;
  slug: string;
  pageType: string;
  // Returns null when the requested filter (e.g. a news category) does not exist.
  getFilterParams?: () => CmsStoriesParams | null;
  getFilterKey?: () => string;
};

const STORIES_PER_PAGE = 10;
const LIST_ITEM_CONTENT_FIELDS = [
  'component',
  'title',
  'subTitle',
  'previewTitle',
  'previewSubTitle',
  'previewImage',
  'publishedDate',
];

function pickListItemContent(content: Record<string, any> = {}) {
  return Object.fromEntries(
    LIST_ITEM_CONTENT_FIELDS.filter((field) => field in content).map((field) => [field, content[field]]),
  );
}

export default async function useMopCmsStoryListPage(options: StoryListPageOptions) {
  const nuxtApp = useNuxtApp();
  const { $apiCms, $gtm, $mopI18n } = nuxtApp;
  const isStoryblokLivePreview = nuxtApp.$storyblokLivePreview?.isEnabled ?? false;
  const route = useRoute();
  const router = useRouter();
  const memoryCache = ssrCache();
  const pageRef = computed(() => route.query?.page as string | undefined);
  const filterKeyRef = computed(() => options.getFilterKey?.() ?? 'all');

  async function fetchStoryList(): Promise<CmsStoryListResponseData> {
    const filterParams = options.getFilterParams ? options.getFilterParams() : {};
    if (filterParams === null) {
      throw createError({ statusCode: 404, statusMessage: 'PAGE_NOT_FOUND' });
    }
    const params: CmsStoriesParams = {
      is_startpage: 0 as any, // type wrong in storyblok package
      per_page: STORIES_PER_PAGE,
      // @ts-ignore
      page: pageRef.value,
      sort_by: 'content.publishedDate:desc',
      ...filterParams,
    };
    const requestStoryList = async () => {
      const response: CmsStoryListResponseData = await $apiCms.getStories(
        `${constants.STORYBLOK.COMPANY_SLUG_PREFIX}${options.slug}`,
        params,
      );
      if (response.error) {
        throw createError({ statusCode: 500, statusMessage: 'CMS_UNAVAILABLE' });
      }
      const storyListModel = cmsStoryListModel(response);
      return {
        data: {
          total: storyListModel.getTotal(),
          data: {
            stories: storyListModel.getData().data.stories.map((story: Record<string, any>) => ({
              ...story,
              content: pickListItemContent(story.content),
            })),
          },
        },
      } as CmsStoryListResponseData;
    };
    if (isClient || isStoryblokLivePreview) {
      return requestStoryList();
    }
    const cacheKey = `${options.cacheKeyPrefix}-${$mopI18n.locale}-${filterKeyRef.value}-${pageRef.value ?? 1}`;
    return (await memoryCache.get<CmsStoryListResponseData>(cacheKey, requestStoryList)) as CmsStoryListResponseData;
  }

  const {
    data: storyListResponseRef,
    error: storyListErrorRef,
    status: storyListStatusRef,
  } = await useAsyncData(() => `${options.cacheKeyPrefix}-${filterKeyRef.value}-${pageRef.value ?? 1}`, fetchStoryList);

  const getErrorStatusCode = () => storyListErrorRef.value?.statusCode ?? 500;
  if (storyListErrorRef.value) {
    throw createError({
      statusCode: getErrorStatusCode(),
      statusMessage: storyListErrorRef.value.statusMessage,
      fatal: true,
    });
  }

  const storyListModelRef = computed(() => cmsStoryListModel(storyListResponseRef.value ?? null));
  const storyListRef = computed(() => storyListModelRef.value.getStoryModelList());
  const totalPagesRef = computed(() => Math.ceil(storyListModelRef.value.getTotal() / STORIES_PER_PAGE));
  const loadingRef = computed(() => ({ loading: storyListStatusRef.value === 'pending' }));

  onMounted(() => {
    watch([filterKeyRef, pageRef], ([filterKey, page], [previousFilterKey, previousPage]) => {
      if (filterKey === previousFilterKey && page !== previousPage) {
        scrollToPosition(0);
      }
    });
    watch(storyListErrorRef, (error) => {
      if (error) {
        showError({ statusCode: getErrorStatusCode(), statusMessage: error.statusMessage });
      }
    });
    watch(
      storyListResponseRef,
      (storyListResponse) => {
        if (!storyListResponse || storyListErrorRef.value) {
          return;
        }
        $gtm.trackPageView({
          pageType: options.pageType,
        });
        if (storyListRef.value.length === 0 && pageRef.value) {
          router.replace(route.path);
        }
      },
      { immediate: true },
    );
  });

  return securedWrap({
    storyListRef,
    totalPagesRef,
    loadingRef,
  });
}
